import { ErpFrame, NumberUtils } from '@ca/common'

import { zodResolver } from '@hookform/resolvers/zod'
import { useCurrentPackageType } from '@order/hooks/usePackages'
import { OrderFrame } from '@order/orderTypes'
import { useGlobalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import MeasurementUtils from 'utils/MeasurementUtils'
import { z } from 'zod'

export const EXT_MIN = -10
export const EXT_MAX = 10

export const getShRange = ({
  packageType,
  frame,
  y_ext,
}: {
  packageType: string
  frame: OrderFrame | ErpFrame
  y_ext: number
}) => {
  const vbox = frame?.vbox || 0
  const vboxOffset = packageType === 'sv' ? 2 : 10
  const max = vbox + y_ext - vboxOffset

  return {
    min: packageType === 'sv' ? 10 : 15,
    max: NumberUtils.round.decimal(max),
  }
}

const getDefaultSh = (frame: OrderFrame | ErpFrame, packageType: string) => {
  if (packageType === 'sv') {
    return (frame.vbox / 2 + 2).toFixed(2)
  }
  return ''
}

export const useFrameSettingsForm = (frame: OrderFrame | ErpFrame) => {
  const { t } = useTranslation(['ui', 'common', 'modals'])

  // This form is outside of the stack so it should use global search params
  const { bundleType } = useGlobalSearchParams<{ bundleType: string }>()
  const packageType = useCurrentPackageType({
    bundle: {
      bundleType,
    },
  })

  const defaultValues = useMemo(() => {
    const frameMeasurements = 'measurement' in frame ? frame.measurement : null
    const frameShape = 'shape' in frame ? frame.shape : null
    const defaultSh = getDefaultSh(frame, packageType)
    const getShVal = (val?: string | null) => {
      if (!val) {
        return defaultSh
      }

      return MeasurementUtils.parse.sh.value(val)?.toFixed(2) || defaultSh
    }
    const getExtVal = (val?: number | null) => {
      if (!val) {
        return ''
      }
      return val.toString()
    }

    return {
      extend_x: getExtVal(frameShape?.extend_x),
      extend_y: getExtVal(frameShape?.extend_y),
      leftSH: getShVal(frameMeasurements?.segmentHeight.leftSH),
      rightSH: getShVal(frameMeasurements?.segmentHeight.rightSH),
    }
  }, [frame, packageType])

  const resolver = useMemo(() => {
    // We need to get range here to create the resolver
    const range = getShRange({
      packageType,
      frame,
      y_ext: Number(defaultValues.extend_y) || 0,
    })

    const sh = z.coerce.number().min(
      range.min,
      t('validation:number.min', {
        value: range.min,
      })
    )

    const ExtendValueSchema = z.preprocess(
      NumberUtils.parse.toFloatOrNull,
      z
        .number()
        .min(
          EXT_MIN,
          t('validation:number.min', {
            value: EXT_MIN,
          })
        )
        .max(
          EXT_MAX,
          t('validation:number.max', {
            value: EXT_MAX,
          })
        )
        .nullable()
    )
    return zodResolver(
      z
        .object({
          extend_x: ExtendValueSchema,
          extend_y: ExtendValueSchema,
          leftSH: sh,
          rightSH: sh,
        })
        .superRefine((data, ctx) => {
          // here we use the actual y_ext value
          const { max } = getShRange({
            packageType,
            frame,
            y_ext: Number(data.extend_y) || 0,
          })
          if (data.leftSH > max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['leftSH'],
              message: t('validation:number.max', {
                value: max,
              }),
            })
          }
          if (data.rightSH > max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rightSH'],
              message: t('validation:number.max', {
                value: max,
              }),
            })
          }
        })
    )
  }, [frame, packageType, t, defaultValues.extend_y])

  const form = useForm({
    defaultValues,
    resolver,
  })
  return {
    form,
    // Makes easy to pass to other components as spread props
    frame,
    packageType,
  }
}

export type FrameSettingsFormHook = ReturnType<typeof useFrameSettingsForm>

export default useFrameSettingsForm
